import Header from '@/components/Header';
import Footer from '@/components/Footer';
import CustomCursor from '@/components/CustomCursor';
import { Toaster } from 'sonner';
import { 
  Upload, FileCheck, Clock, Shield, ArrowRight, CheckCircle,
  FileText, Users, BarChart3, Lock, Zap, AlertTriangle,
  Mail, XCircle, FolderOpen, Search
} from 'lucide-react';

const AcquisitionPortalPage = () => {
  const portalStats = [
    { metric: '95%+', label: 'Submission accuracy' },
    { metric: '< 5 min', label: 'Package validation' },
    { metric: '100%', label: 'Audit trail coverage' },
    { metric: 'Zero', label: 'Lost attachments' }
  ];

  const portalFeatures = [
    {
      icon: Upload,
      title: 'Structured Submissions',
      description: 'Upload proposals with standardized naming, organized sections, and completeness validation.',
      color: 'from-blue-500 to-cyan-600'
    },
    {
      icon: FileCheck,
      title: 'Completeness Checks',
      description: 'Automated verification ensures all required documents and sections are included before submission.',
      color: 'from-emerald-500 to-teal-600'
    },
    {
      icon: Clock,
      title: 'Real-Time Status',
      description: 'Track submission status from draft through evaluation with live updates and notifications.',
      color: 'from-purple-500 to-indigo-600'
    },
    {
      icon: Shield,
      title: 'Full Audit Trail',
      description: 'Every action logged with timestamps. Who submitted what, when, and all version history.',
      color: 'from-orange-500 to-red-600'
    }
  ];

  const submissionTypes = [
    {
      type: 'RFI Response',
      description: 'Submit market research responses and capability statements',
      icon: FileText
    },
    {
      type: 'RFQ Package',
      description: 'Complete quote packages including pricing and terms',
      icon: BarChart3
    },
    {
      type: 'Full RFP Proposal',
      description: 'Technical, management, past performance, and price volumes',
      icon: Users
    },
    {
      type: 'SOW Response',
      description: 'Detailed task-by-task approach and staffing plans',
      icon: CheckCircle
    }
  ];

  const securityFeatures = [
    'AES-256 encryption at rest',
    'TLS 1.3 in transit',
    'FedRAMP-authorized infrastructure',
    'Role-based access controls',
    'SSO integration support',
    'Continuous security monitoring'
  ];

  const workflowSteps = [
    {
      step: '1',
      title: 'Create Submission',
      description: 'Start a new submission and select the acquisition type (RFI, RFQ, RFP, SOW).'
    },
    {
      step: '2',
      title: 'Upload Documents',
      description: 'Add required volumes, attachments, and supporting materials with guided prompts.'
    },
    {
      step: '3',
      title: 'Validate Package',
      description: 'System checks for completeness, naming conventions, and required fields.'
    },
    {
      step: '4',
      title: 'Submit & Track',
      description: 'Submit your package and monitor status through evaluation.'
    }
  ];

  const emailChaos = [
    { icon: Mail, text: '200+ emails per submission', color: 'red' },
    { icon: FolderOpen, text: 'Attachments lost in threads', color: 'orange' },
    { icon: Search, text: '"Which version is final?"', color: 'amber' },
    { icon: XCircle, text: 'Missing docs found at deadline', color: 'red' }
  ];

  const portalOrder = [
    'Single source of truth for all submission materials',
    'Automatic version control. No more "final_v3_REAL"',
    'Completeness checks before you can submit',
    'Instant receipt confirmation with audit trail'
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-indigo-50">
      <CustomCursor />
      <Toaster position="top-center" />
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="relative pt-20 pb-24 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-100/50 via-transparent to-purple-100/50"></div>
          <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <span className="inline-block px-4 py-1.5 bg-indigo-100 text-indigo-700 text-sm font-semibold rounded-full mb-6">
                  Acquisition Portal
                </span>
                <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                  Replace Inbox Chaos with
                  <span className="block text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-600">
                    Structured Submissions
                  </span>
                </h1>
                <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                  The Automated Submission Portal handles full RFP proposals (technical volumes, pricing, past performance, and more) with completeness checks and a complete audit trail.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <a
                    href="/contact"
                    className="px-8 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold rounded-full hover:shadow-lg hover:shadow-purple-500/30 transition-all hover:-translate-y-0.5 text-center"
                  >
                    Request Access
                  </a>
                  <a
                    href="/product"
                    className="px-8 py-3 bg-white border-2 border-gray-200 text-gray-700 font-semibold rounded-full hover:border-purple-300 hover:text-purple-700 transition-all text-center"
                  >
                    Learn More
                  </a>
                </div>
              </div>
              
              {/* Portal Preview */}
              <div className="relative">
                <div className="bg-white rounded-2xl shadow-2xl p-6 border border-gray-100">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-3 h-3 rounded-full bg-red-400"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                    <div className="w-3 h-3 rounded-full bg-green-400"></div>
                    <span className="ml-4 text-sm text-gray-500">Submission Portal</span>
                  </div>
                  
                  {/* Mock Portal UI */}
                  <div className="space-y-4">
                    <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg p-4 border border-purple-100">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold text-gray-900">RFP-2024-0542</span>
                        <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs font-medium rounded-full">Ready to Submit</span>
                      </div>
                      <div className="text-sm text-gray-600">IT Modernization Services</div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-gray-50 rounded-lg p-3">
                        <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                          <FileText className="w-4 h-4" />
                          Technical Volume
                        </div>
                        <div className="flex items-center gap-1">
                          <CheckCircle className="w-4 h-4 text-emerald-500" />
                          <span className="text-xs text-emerald-600">Complete</span>
                        </div>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3">
                        <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                          <BarChart3 className="w-4 h-4" />
                          Price Volume
                        </div>
                        <div className="flex items-center gap-1">
                          <CheckCircle className="w-4 h-4 text-emerald-500" />
                          <span className="text-xs text-emerald-600">Complete</span>
                        </div>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3">
                        <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                          <Users className="w-4 h-4" />
                          Past Performance
                        </div>
                        <div className="flex items-center gap-1">
                          <CheckCircle className="w-4 h-4 text-emerald-500" />
                          <span className="text-xs text-emerald-600">Complete</span>
                        </div>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3">
                        <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                          <FileCheck className="w-4 h-4" />
                          Certifications
                        </div>
                        <div className="flex items-center gap-1">
                          <AlertTriangle className="w-4 h-4 text-amber-500" />
                          <span className="text-xs text-amber-600">1 Missing</span>
                        </div>
                      </div>
                    </div>
                    
                    <button className="w-full py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold rounded-lg hover:shadow-lg transition-all">
                      Review & Submit
                    </button>
                  </div>
                </div>
                <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-gradient-to-br from-purple-400 to-blue-500 rounded-full opacity-20 blur-2xl"></div>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Strip */}
        <section className="py-10 bg-gradient-to-r from-indigo-600 via-purple-600 to-blue-600">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {portalStats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-3xl sm:text-4xl font-bold text-white mb-2">{stat.metric}</div>
                  <div className="text-sm text-indigo-100">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Before/After Email Chaos Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                From Email{' '}
                <span className="text-red-600">Chaos</span>{' '}
                to Submission{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-600">Confidence</span>
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                The average RFP submission involves 200+ emails, multiple versions, and last-minute panic over missing attachments. Sound familiar?
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
              {/* Email Chaos Side */}
              <div className="relative h-full">
                <div className="absolute inset-0 bg-red-100 rounded-3xl transform rotate-1"></div>
                <div className="relative bg-white rounded-2xl p-8 border-2 border-red-200 shadow-lg h-full flex flex-col">
                  <h3 className="text-xl font-bold text-red-700 mb-6 flex items-center gap-2">
                    <span className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center text-red-600">✗</span>
                    The Email Way
                  </h3>
                  
                  <div className="space-y-4 mb-6 flex-1">
                    {emailChaos.map((item, index) => (
                      <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                        <item.icon className={`w-6 h-6 text-${item.color}-500`} />
                        <span className="text-gray-700">{item.text}</span>
                      </div>
                    ))}
                  </div>

                  <div className="bg-red-50 rounded-xl p-4 border border-red-200">
                    <p className="text-red-800 text-sm italic">
                      &quot;We found out we were missing a key attachment 30 minutes before the deadline.&quot;
                    </p>
                    <p className="text-red-600 text-xs mt-2">Every acquisition team, at some point</p>
                  </div>
                </div>
              </div>

              {/* Portal Order Side */}
              <div className="relative h-full">
                <div className="absolute inset-0 bg-gradient-to-br from-purple-100 to-blue-100 rounded-3xl transform -rotate-1"></div>
                <div className="relative bg-white rounded-2xl p-8 border-2 border-purple-200 shadow-lg h-full flex flex-col">
                  <h3 className="text-xl font-bold text-purple-700 mb-6 flex items-center gap-2">
                    <span className="w-8 h-8 bg-gradient-to-br from-purple-500 to-blue-500 rounded-full flex items-center justify-center text-white">
                      <Zap className="w-4 h-4" />
                    </span>
                    The Portal Way
                  </h3>
                  
                  <div className="space-y-4 mb-6 flex-1">
                    {portalOrder.map((item, index) => (
                      <div key={index} className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg">
                        <CheckCircle className="w-6 h-6 text-emerald-500" />
                        <span className="text-gray-700">{item}</span>
                      </div>
                    ))}
                  </div>

                  <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-xl p-4 text-white">
                    <p className="text-sm font-medium">
                      Submit with confidence. Every. Single. Time.
                    </p>
                    <p className="text-purple-200 text-xs mt-2">No more deadline panic.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-20 bg-gradient-to-br from-gray-50 to-indigo-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                Everything You Need for Compliant Submissions
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                From structured uploads to complete audit trails, the portal handles the heavy lifting.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {portalFeatures.map((feature, index) => (
                <div
                  key={index}
                  className="group bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-xl hover:border-purple-200 transition-all duration-300"
                  data-robot-tooltip={feature.description}
                >
                  <div className={`inline-flex p-3 rounded-xl bg-gradient-to-br ${feature.color} mb-4`}>
                    <feature.icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-purple-700 transition-colors">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Compliance Built-In Dark Section */}
        <section className="py-20 bg-gradient-to-br from-slate-900 via-indigo-900 to-slate-900">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 font-medium rounded-full mb-6">
                <Shield className="w-5 h-5" />
                <span>Compliance Built-In</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
                Submissions That{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-400">
                  Survive Scrutiny
                </span>
              </h2>
              <p className="text-xl text-slate-300 max-w-3xl mx-auto">
                Every submission is automatically checked, validated, and documented. 
                When questions arise, you have answers.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
                <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center mb-6">
                  <FileCheck className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Pre-Submission Validation</h3>
                <p className="text-slate-400">
                  Missing documents? Wrong formats? Incomplete sections? The portal catches it before you submit. Not after.
                </p>
              </div>

              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
                <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl flex items-center justify-center mb-6">
                  <Clock className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Timestamped Everything</h3>
                <p className="text-slate-400">
                  Who uploaded what, when, and from where. Every action logged. Every version tracked. Complete chain of custody.
                </p>
              </div>

              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
                <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl flex items-center justify-center mb-6">
                  <AlertTriangle className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Protest-Ready Documentation</h3>
                <p className="text-slate-400">
                  If questions arise, you have irrefutable evidence of exactly what was submitted and when. No ambiguity.
                </p>
              </div>
            </div>

            <div className="mt-12 text-center">
              <div className="inline-flex items-center gap-3 bg-emerald-500/20 border border-emerald-500/30 rounded-full px-6 py-3">
                <CheckCircle className="w-6 h-6 text-emerald-400" />
                <span className="text-emerald-300 font-semibold">
                  &quot;Every submission is audit-ready from day one.&quot;
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Workflow Steps */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                Simple, Guided Submission Process
              </h2>
              <p className="text-lg text-gray-600">
                Four steps from draft to delivered
              </p>
            </div>
            
            <div className="grid md:grid-cols-4 gap-8">
              {workflowSteps.map((step, index) => (
                <div key={index} className="relative">
                  <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100 h-full hover:shadow-lg hover:border-purple-200 transition-all">
                    <div className="w-10 h-10 bg-gradient-to-br from-purple-600 to-blue-600 rounded-full flex items-center justify-center text-white font-bold mb-4">
                      {step.step}
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">
                      {step.title}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {step.description}
                    </p>
                  </div>
                  {index < 3 && (
                    <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                      <ArrowRight className="w-6 h-6 text-purple-300" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Submission Types */}
        <section className="py-20 bg-gradient-to-br from-gray-50 to-indigo-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                  Supports All Acquisition Types
                </h2>
                <p className="text-lg text-gray-600 mb-8">
                  Whether you&apos;re responding to an RFI or submitting a full multi-volume RFP proposal, the portal adapts to your needs.
                </p>
                <div className="space-y-4">
                  {submissionTypes.map((type, index) => (
                    <div key={index} className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md hover:border-purple-200 transition-all">
                      <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-purple-100 to-indigo-100 rounded-lg flex items-center justify-center">
                        <type.icon className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">{type.type}</h4>
                        <p className="text-sm text-gray-600">{type.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Security Section */}
              <div className="bg-gradient-to-br from-gray-900 to-indigo-900 rounded-2xl p-8 text-white">
                <div className="flex items-center gap-3 mb-6">
                  <Lock className="w-8 h-8 text-purple-400" />
                  <h3 className="text-2xl font-bold">Enterprise Security</h3>
                </div>
                <p className="text-gray-300 mb-8">
                  Your submissions are protected by the same security infrastructure trusted by federal agencies.
                </p>
                <ul className="space-y-3">
                  {securityFeatures.map((feature, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                      <span className="text-gray-200">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-purple-600 to-blue-600">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <Zap className="w-12 h-12 text-white/80 mx-auto mb-6" />
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Ready to Streamline Your Submissions?
            </h2>
            <p className="text-lg text-purple-100 mb-8">
              Join organizations that have replaced email chaos with structured, auditable submissions.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contact"
                className="px-8 py-3 bg-white text-purple-700 font-semibold rounded-full hover:shadow-lg transition-all hover:-translate-y-0.5"
              >
                Request Access
              </a>
              <a
                href="/product"
                className="px-8 py-3 bg-transparent border-2 border-white text-white font-semibold rounded-full hover:bg-white/10 transition-all"
              >
                View Full Product
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default AcquisitionPortalPage;
